import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@dropbox/dig-components/buttons';
import { RadioButton } from '@dropbox/dig-components/controls';
import { Modal } from '@dropbox/dig-components/modal';
import { TextInput } from '@dropbox/dig-components/text_fields';
import { Text } from '@dropbox/dig-components/typography';
import { Box, Stack } from '@dropbox/dig-foundations';
import { getBrandName } from 'js/sign-components/common/brand';
import { cancellationSurveyReasonCodes } from './types';
import type { CancellationReason, CancellationSurveyResponse } from './types';

const confirmationMessages = defineMessages({
  title: {
    id: '',
    description: 'Title for the final cancellation confirmation modal.',
    defaultMessage: 'Confirm cancellation',
  },
  description: {
    id: '',
    description:
      'Explains what happens when the customer confirms cancellation.',
    defaultMessage:
      'You’re about to cancel your {planName} plan. When your current billing period ends on {endDate, date, long}, you’ll lose access to all {planName} plan features. Confirm to proceed.',
  },
  dontCancel: {
    id: '',
    description: 'Button to dismiss cancellation confirmation.',
    defaultMessage: 'Don’t cancel',
  },
  confirm: {
    id: '',
    description: 'Button that confirms cancellation in the final modal.',
    defaultMessage: 'Confirm cancellation',
  },
  close: {
    id: '',
    description: 'Accessible label for closing the cancellation modal.',
    defaultMessage: 'Close',
  },
});

export type ConfirmationModalProps = {
  isOpen: boolean;
  planName: string;
  planEndDate: number;
  onRequestClose: () => void;
  onDontCancel: () => void;
  onConfirmCancel: () => void;
};

export function ConfirmationModal({
  isOpen,
  planName,
  planEndDate,
  onRequestClose,
  onDontCancel,
  onConfirmCancel,
}: ConfirmationModalProps) {
  const intl = useIntl();

  return (
    <Modal
      open={isOpen}
      isCentered
      width={620}
      onRequestClose={onRequestClose}
      withCloseButton={intl.formatMessage(confirmationMessages.close)}
      aria-labelledby="cancellation-confirmation-modal-title"
      aria-describedby="cancellation-confirmation-modal-description"
    >
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title id="cancellation-confirmation-modal-title">
          <FormattedMessage {...confirmationMessages.title} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="cancellation-confirmation-modal-description">
        <Text>
          <FormattedMessage
            {...confirmationMessages.description}
            values={{ planName, endDate: planEndDate }}
          />
        </Text>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="opacity" onClick={onDontCancel}>
          <FormattedMessage {...confirmationMessages.dontCancel} />
        </Button>
        <Button variant="primary" onClick={onConfirmCancel}>
          <FormattedMessage {...confirmationMessages.confirm} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const messages = defineMessages({
  title: {
    id: '',
    description: 'Title for the cancellation survey modal.',
    defaultMessage: 'Are you sure you want to cancel?',
  },
  introduction: {
    id: '',
    description: 'Introduction asking a customer why they are canceling.',
    defaultMessage:
      'Hey {userName}, we are sorry you’re thinking of leaving us. Would you tell us why? We might be able to help!',
  },
  reasonPrompt: {
    id: '',
    description: 'Prompt above the cancellation survey reasons.',
    defaultMessage: 'Please select one reason for canceling',
  },
  temporary: {
    id: '',
    description: 'Reason for canceling because the service was temporary.',
    defaultMessage: 'Needed {brandName} temporarily',
  },
  cost: {
    id: '',
    description: 'Reason for canceling because the service is too expensive.',
    defaultMessage: 'Too expensive',
  },
  unusedLicenses: {
    id: '',
    description: 'Reason for canceling because licenses are not being used.',
    defaultMessage: 'Paying for unused licenses',
  },
  alternative: {
    id: '',
    description: 'Reason for canceling to switch to another service.',
    defaultMessage: 'Switching to an alternative',
  },
  technical: {
    id: '',
    description: 'Reason for canceling because of technical or billing issues.',
    defaultMessage: 'Technical or billing issues',
  },
  noLongerNeed: {
    id: '',
    description:
      'Reason for canceling because the customer no longer needs it.',
    defaultMessage: 'No longer need',
  },
  customerService: {
    id: '',
    description: 'Reason for canceling because of poor customer service.',
    defaultMessage: 'Poor customer service',
  },
  other: {
    id: '',
    description: 'Other reason for canceling.',
    defaultMessage: 'Other (share below)',
  },
  otherPlaceholder: {
    id: '',
    description:
      'Placeholder for more detail about another cancellation reason.',
    defaultMessage: 'I am canceling because...',
  },
  skip: {
    id: '',
    description: 'Button to skip the cancellation survey.',
    defaultMessage: 'Skip',
  },
  dontCancel: {
    id: '',
    description: 'Button to close the cancellation survey.',
    defaultMessage: 'Don’t cancel',
  },
  cancelPlan: {
    id: '',
    description: 'Button to proceed to the cancellation confirmation modal.',
    defaultMessage: 'Cancel plan',
  },
  continueCancelling: {
    id: '',
    description: 'Button to continue to the cancellation page from the survey.',
    defaultMessage: 'Continue cancelling',
  },
  close: {
    id: '',
    description: 'Accessible label for closing the cancellation modal.',
    defaultMessage: 'Close',
  },
});

const reasons = [
  [cancellationSurveyReasonCodes.TEMPORARY, messages.temporary],
  [cancellationSurveyReasonCodes.COST, messages.cost],
  [cancellationSurveyReasonCodes.UNUSED_LICENSES, messages.unusedLicenses],
  [cancellationSurveyReasonCodes.ALTERNATIVE, messages.alternative],
  [cancellationSurveyReasonCodes.TECHNICAL, messages.technical],
  [cancellationSurveyReasonCodes.NO_LONGER_NEED, messages.noLongerNeed],
  [cancellationSurveyReasonCodes.CUSTOMER_SERVICE, messages.customerService],
  [cancellationSurveyReasonCodes.OTHER, messages.other],
] as const;

type SurveyModalBaseProps = {
  isOpen: boolean;
  userName: string;
  planName: string;
  planEndDate: number;
  onRequestClose: () => void;
  onDontCancel: () => void;
};

export type SurveyModalProps = SurveyModalBaseProps &
  (
    | {
        onContinue: (response: CancellationSurveyResponse) => void;
        onConfirmCancel?: never;
        onSkip?: never;
      }
    | {
        onContinue?: never;
        onConfirmCancel: (response?: CancellationSurveyResponse) => void;
        onSkip?: () => void;
      }
  );

function CancellationSurveyModal({
  isOpen,
  userName,
  onRequestClose,
  onDontCancel,
  onSkip,
  onContinue,
  onConfirmCancel,
}: SurveyModalProps) {
  const intl = useIntl();
  const [selectedReason, setSelectedReason] =
    useState<CancellationReason | null>(null);
  const [context, setContext] = useState('');

  return (
    <Modal
      open={isOpen}
      isCentered
      width={700}
      onRequestClose={onRequestClose}
      withCloseButton={intl.formatMessage(messages.close)}
      aria-labelledby="cancellation-survey-modal-title"
      aria-describedby="cancellation-survey-modal-description"
    >
      <Modal.Header hasBottomSpacing="title-standard">
        <Modal.Title id="cancellation-survey-modal-title">
          <FormattedMessage {...messages.title} />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="cancellation-survey-modal-description">
        <Stack gap="Micro Large">
          <Stack gap="Macro Small" display="flex">
            <Text>
              <FormattedMessage
                {...messages.introduction}
                values={{ userName }}
              />
            </Text>
            <Text isBold>
              <FormattedMessage {...messages.reasonPrompt} />
            </Text>
          </Stack>
          <Stack
            gap="Micro Medium"
            role="radiogroup"
            aria-label={intl.formatMessage(messages.reasonPrompt)}
          >
            {reasons.map(([value, label]) => (
              <Box key={value}>
                <Box display="flex" alignItems="center" columnGap="Micro Small">
                  <RadioButton
                    checked={selectedReason === value}
                    id={`cancellation-survey-reason-${value}`}
                    name="cancellation-reason"
                    value={value}
                    onChange={() => setSelectedReason(value)}
                    style={{ margin: 0 }}
                  />
                  <Text
                    tagName="label"
                    htmlFor={`cancellation-survey-reason-${value}`}
                  >
                    <FormattedMessage
                      {...label}
                      values={{ brandName: getBrandName('S') }}
                    />
                  </Text>
                </Box>
                {value === 'other' && (
                  <Box paddingLeft="Macro Medium" paddingTop="Micro Small">
                    <TextInput
                      aria-label={intl.formatMessage(messages.otherPlaceholder)}
                      placeholder={intl.formatMessage(
                        messages.otherPlaceholder,
                      )}
                      value={context}
                      onFocus={() => setSelectedReason('other')}
                      onChange={(event) =>
                        setContext(event.currentTarget.value)
                      }
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        {onSkip !== undefined && (
          <Button variant="transparent" onClick={onSkip}>
            <FormattedMessage {...messages.skip} />
          </Button>
        )}
        <Button variant="opacity" onClick={onDontCancel}>
          <FormattedMessage {...messages.dontCancel} />
        </Button>
        {onContinue ? (
          <Button
            variant="primary"
            onClick={() => onContinue({ reason: selectedReason, context })}
          >
            <FormattedMessage {...messages.continueCancelling} />
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => onConfirmCancel({ reason: selectedReason, context })}
          >
            <FormattedMessage {...messages.cancelPlan} />
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function SurveyModal(props: SurveyModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!props.isOpen) {
      setIsConfirming(false);
    }
  }, [props.isOpen]);

  const onSkip = props.onSkip
    ? () => {
        setIsConfirming(true);
        props.onSkip?.();
      }
    : undefined;

  if (!props.isOpen) {
    return null;
  }

  if (isConfirming && props.onConfirmCancel) {
    return (
      <ConfirmationModal
        isOpen
        planName={props.planName}
        planEndDate={props.planEndDate}
        onRequestClose={props.onRequestClose}
        onDontCancel={props.onDontCancel}
        onConfirmCancel={() => props.onConfirmCancel()}
      />
    );
  }

  if (props.onContinue) {
    return <CancellationSurveyModal {...props} />;
  }

  return <CancellationSurveyModal {...props} onSkip={onSkip} />;
}

export default SurveyModal;
